import { Flex, Heading, ScaleFade, Text } from '@chakra-ui/react';
import { ColorData } from '@texas/api/endpoints/compositionApi';
import { moveDownRotateAnimation } from '@texas/resources/animations/animations';
import { rudholmMatrixColors } from '@texas/resources/colors/rudholmColors';
import { Icons, defaultIconSize } from '../Icons';
import { ColorComponent } from './ColorComponent';
import { useTranslation } from 'react-i18next';
import { onlyUnique } from '@texas/utils/helpers/commonHelpers';
import { Color, ColorType } from '@texas/api/endpoints/metadata/colorsApi';

export function RecommendedColors({
  matrixNodeValue,
  pickedColors,
  metalShades,
  colorPicked,
}: {
  matrixNodeValue?: string;
  pickedColors: ColorData[];
  metalShades: Color[];
  colorPicked: (color: ColorData) => void;
}) {
  const { t } = useTranslation();
  const codes = matrixNodeValue?.split(':')[0].match(/.{1,2}/g) ?? [];
  const foundColorIndices = codes
    .map<{ index: number; type: ColorType }>((c) => {
      let index = rudholmMatrixColors.colors.findIndex(
        (x) => x.code === c && !pickedColors.some((p) => p.code === x.code),
      );

      if (index > -1) {
        return { index, type: ColorType.RudholmMatrix };
      }

      index = metalShades.findIndex(
        (x) => x.code === c && !pickedColors.some((p) => p.code === x.code),
      );

      return { index, type: ColorType.MetalShade };
    })
    .filter((x) => x.index !== -1)
    .filter((x, i, arr) =>
      onlyUnique(
        x.index,
        i,
        arr.map((a) => a.index),
      ),
    );

  const show = matrixNodeValue !== undefined && foundColorIndices.length > 0;

  return (
    <ScaleFade initialScale={0.9} in={show}>
      <Flex
        flexDir="column"
        gap={2}
        bg="texas.bg.800"
        _light={{ bg: 'gray.10' }}
        borderRadius="md"
        padding={4}
      >
        <Flex gap={2} align="center">
          <Icons.ThumbUp
            animation={moveDownRotateAnimation(1000)}
            boxSize={defaultIconSize}
          />
          <Heading fontSize="md">{t('colors.recommended')}</Heading>
        </Flex>
        <Text>{t('colors.recommendedDesc')}</Text>
        {show && (
          <>
            {foundColorIndices.map((x) => {
              const color =
                x.type === ColorType.RudholmMatrix
                  ? rudholmMatrixColors.colors[x.index]
                  : metalShades[x.index];

              return (
                <ColorComponent
                  key={x.index}
                  color={color}
                  onSelect={() =>
                    colorPicked({
                      code: color.code,
                      colorType: x.type,
                      description: color.description ?? '',
                      hex: color.hex,
                      name: color.name,
                    })
                  }
                  selected={false}
                />
              );
            })}
          </>
        )}
      </Flex>
    </ScaleFade>
  );
}
